import { useMemo, useState } from 'react';
import { Percent, ArrowDownRight } from 'lucide-react';
import { eur } from '../../lib/api';
import { TableScroll } from './TableScroll';

const MONTHS_PT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

/**
 * Side-by-side comparison of Y1 monthly numbers with vs without discount.
 * The engine works at annual granularity, so the monthly split is a linear
 * one-twelfth of the yearly totals (identical business seasonality assumed).
 * The absolute delta and its cumulative sum are what really matter for
 * decision-making.
 */
export function DiscountComparator({ scenariosNoDiscount, scenariosWithDiscount, scenarios }) {
  const keys = Object.keys(scenarios || {});
  const [active, setActive] = useState(keys[0] || 'conservative');

  const rows = useMemo(() => {
    const no = scenariosNoDiscount?.[active]?.years?.[0];
    const yes = scenariosWithDiscount?.[active]?.years?.[0];
    if (!no || !yes) return null;
    const perMonth = (annual) => (Number(annual) || 0) / 12;
    let cumulativeDelta = 0;
    return MONTHS_PT.map((label, i) => {
      const noRev = perMonth(no.revenue_net);
      const yesRev = perMonth(yes.revenue_net);
      const noEbitda = perMonth(no.ebitda);
      const yesEbitda = perMonth(yes.ebitda);
      const deltaRev = yesRev - noRev;
      const deltaEbitda = yesEbitda - noEbitda;
      cumulativeDelta += deltaEbitda;
      return {
        month: i + 1,
        label,
        noRev,
        yesRev,
        deltaRev,
        noEbitda,
        yesEbitda,
        deltaEbitda,
        cumulativeDelta,
      };
    });
  }, [active, scenariosNoDiscount, scenariosWithDiscount]);

  if (!rows) return null;

  const noYear = scenariosNoDiscount?.[active]?.years?.[0];
  const yesYear = scenariosWithDiscount?.[active]?.years?.[0];
  const yearDeltaRev = (yesYear?.revenue_net || 0) - (noYear?.revenue_net || 0);
  const yearDeltaEbitda = (yesYear?.ebitda || 0) - (noYear?.ebitda || 0);
  const revDropPct = noYear?.revenue_net ? (yearDeltaRev / noYear.revenue_net) * 100 : 0;
  const activeColor = scenarios?.[active]?.color || '#334155';
  const negModels = (yesYear?.per_model || []).filter((m) => m.contribution_negative);

  // Compute max absolute value for scaling the tiny bar chart. Because monthly
  // distribution is linear, the max is the same across all months.
  const maxRev = Math.max(rows[0].noRev, rows[0].yesRev, 1);

  return (
    <section id="comparador-desconto" className="acq-section">
      <div className="acq-container">
        <header>
          <span className="kicker" style={{ color: activeColor }}>
            <Percent size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            Comparador de descontos
          </span>
          <h2>Desconto documental ou uma redução adicional? O impacto, mês a mês.</h2>
          <p className="section-lead">
            Os preços de cada modelo já incluem o <strong>desconto documental</strong> (fonte: Folha1 — I46:I51).
            A coluna <em>base</em> reflete esse preço final. A coluna <em>+ desc. adicional</em> mostra o efeito de
            aplicar, por cima, uma <strong>redução extra</strong> igual ao desconto de cada modelo — uma simulação,
            não o preço praticado. A distribuição mensal é linear (1/12 do total anual).
          </p>
        </header>
        <div className="body">
          <div className="discount-comparator-tabs" role="tablist" data-testid="discount-comparator-tabs">
            {keys.map((key) => {
              const sc = scenarios[key];
              return (
                <button
                  key={key}
                  role="tab"
                  className="scenario-tab"
                  aria-pressed={active === key}
                  onClick={() => setActive(key)}
                  data-testid={`discount-comparator-scenario-${key}`}
                  style={active === key ? { boxShadow: `inset 0 -3px 0 0 ${sc.color || '#334155'}` } : {}}
                >
                  <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: sc.color || '#334155', marginRight: 6, verticalAlign: 'middle' }} />
                  {sc.label}
                </button>
              );
            })}
          </div>

          <div className="discount-comparator-summary" data-testid="discount-comparator-summary">
            <div className="discount-summary-card" style={{ borderTop: `3px solid ${activeColor}` }}>
              <span>RECEITA ANO 1 · BASE</span>
              <strong>{eur(noYear?.revenue_net || 0, 0)}</strong>
              <small>Preço final, desconto documental já aplicado.</small>
            </div>
            <div className="discount-summary-card" style={{ borderTop: '3px solid #b45309' }}>
              <span>+ DESCONTO ADICIONAL</span>
              <strong>{eur(yesYear?.revenue_net || 0, 0)}</strong>
              <small>Redução extra simulada por modelo.</small>
            </div>
            <div className="discount-summary-card discount-summary-card--delta">
              <span>DIFERENÇA</span>
              <strong className={yearDeltaRev < 0 ? 'negative' : ''}>{eur(yearDeltaRev, 0)}</strong>
              <small>{revDropPct.toFixed(1)}% de queda extra · EBITDA {eur(yearDeltaEbitda, 0)}</small>
            </div>
          </div>

          {negModels.length > 0 && (
            <div className="alert alert-warn" data-testid="discount-negative-warning" style={{ marginTop: '1rem' }}>
              <div>
                <strong>Atenção às margens:</strong> com o desconto adicional, {negModels.map((m) => `${m.name} (${eur(m.unit_contribution, 2)}/un.)`).join(', ')} {negModels.length > 1 ? 'passam' : 'passa'} a contribuição negativa, após o custo com buffer e a provisão variável de 150 € e antes dos custos fixos. Uma redução extra torna {negModels.length > 1 ? 'estes modelos deficitários' : 'este modelo deficitário'}.
              </div>
            </div>
          )}

          <TableScroll style={{ marginTop: '1.5rem' }}>
            <table className="data-table discount-comparator-table" data-testid="discount-comparator-table">
              <thead>
                <tr>
                  <th rowSpan={2}>Mês</th>
                  <th colSpan={2} className="num">Receita mensal</th>
                  <th rowSpan={2} className="num">Δ Receita</th>
                  <th colSpan={2} className="num">EBITDA mensal</th>
                  <th rowSpan={2} className="num">Δ EBITDA</th>
                  <th rowSpan={2} className="num">Δ acum. EBITDA</th>
                </tr>
                <tr>
                  <th className="num sub">base</th>
                  <th className="num sub">+ desc. adic.</th>
                  <th className="num sub">base</th>
                  <th className="num sub">+ desc. adic.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.month} data-testid={`discount-comparator-row-${r.month}`}>
                    <td>
                      <div className="discount-row-month">
                        <strong>{r.label}</strong>
                        <div className="discount-row-bars" title="Comparação visual: base (topo) vs com desconto adicional (baixo)">
                          <span className="bar-no" style={{ width: `${Math.min(100, (r.noRev / maxRev) * 100)}%`, background: activeColor }} />
                          <span className="bar-yes" style={{ width: `${Math.min(100, (r.yesRev / maxRev) * 100)}%`, background: '#b45309' }} />
                        </div>
                      </div>
                    </td>
                    <td className="num">{eur(r.noRev, 0)}</td>
                    <td className="num">{eur(r.yesRev, 0)}</td>
                    <td className={`num ${r.deltaRev < 0 ? 'delta-neg' : 'delta-pos'}`}>
                      <ArrowDownRight size={11} style={{ display: r.deltaRev < 0 ? 'inline' : 'none', verticalAlign: 'middle', marginRight: 2 }} />
                      {eur(r.deltaRev, 0)}
                    </td>
                    <td className="num">{eur(r.noEbitda, 0)}</td>
                    <td className="num">{eur(r.yesEbitda, 0)}</td>
                    <td className={`num ${r.deltaEbitda < 0 ? 'delta-neg' : 'delta-pos'}`}>{eur(r.deltaEbitda, 0)}</td>
                    <td className={`num ${r.cumulativeDelta < 0 ? 'delta-neg' : 'delta-pos'}`}>{eur(r.cumulativeDelta, 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total">
                  <td><strong>Ano 1</strong></td>
                  <td className="num">{eur(noYear?.revenue_net || 0, 0)}</td>
                  <td className="num">{eur(yesYear?.revenue_net || 0, 0)}</td>
                  <td className={`num ${yearDeltaRev < 0 ? 'delta-neg' : 'delta-pos'}`}>{eur(yearDeltaRev, 0)}</td>
                  <td className="num">{eur(noYear?.ebitda || 0, 0)}</td>
                  <td className="num">{eur(yesYear?.ebitda || 0, 0)}</td>
                  <td className={`num ${yearDeltaEbitda < 0 ? 'delta-neg' : 'delta-pos'}`}>{eur(yearDeltaEbitda, 0)}</td>
                  <td className={`num ${yearDeltaEbitda < 0 ? 'delta-neg' : 'delta-pos'}`}>{eur(yearDeltaEbitda, 0)}</td>
                </tr>
              </tfoot>
            </table>
          </TableScroll>

          <p className="discount-comparator-note">
            <strong>Nota metodológica.</strong> A distribuição mensal é a média simples do valor anual
            (÷ 12). Não é uma previsão sazonal — para uma leitura fiel do padrão real, use o editor
            no modo Apresentação, onde cada mês tem quantidades independentes.
          </p>
        </div>
      </div>
    </section>
  );
}
